import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/layout/AppShell";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Icon from "../../components/ui/Icon";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAlert } from "../../contexts/AlertContext";
import { Parse } from "../../lib/parse";
import CertificateStatusBadge from "../../components/admin/CertificateStatusBadge";
import CertificateUploadModal from "../../components/admin/CertificateUploadModal";
import GroupMemberModal from "../../components/admin/GroupMemberModal";

/**
 * Inline component for searching users to assign a certificate.
 */
function UserCertSearch({ onSelectUser, existingUserIds = [] }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (value) => {
    setQuery(value);
    if (!value || value.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await Parse.Cloud.run("searchTenantUsers", { query: value.trim() });
      const filtered = (res.users || []).filter(
        (u) => !existingUserIds.includes(u.objectId)
      );
      setResults(filtered);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="mt-4">
      <input
        type="text"
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={t("cert-mgmt.search-users", { defaultValue: "Search users by name or email" })}
        className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
      />
      {searching && (
        <p className="mt-1 text-xs text-app-ink-muted">{t("loading", { defaultValue: "Loading..." })}</p>
      )}
      {results.length > 0 && (
        <ul className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-app-line bg-white divide-y divide-app-line">
          {results.map((user) => (
            <li key={user.objectId} className="flex items-center justify-between px-3 py-2">
              <div>
                <p className="text-sm font-medium text-app-ink">{user.Name}</p>
                <p className="text-xs text-app-ink-muted">{user.Email}</p>
              </div>
              <Button
                className="text-xs"
                onClick={() => {
                  onSelectUser(user);
                  setQuery("");
                  setResults([]);
                }}
              >
                <Icon name="plus" className="mr-1 h-3.5 w-3.5" />
                {t("cert-mgmt.upload-cert", { defaultValue: "Upload Cert" })}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CertificateManagementPage() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlert();

  // Admin check
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("Extand_Class");
      const extClass = raw ? JSON.parse(raw) : null;
      const user = Array.isArray(extClass) ? extClass[0] : extClass;
      const role = user?.UserRole || "";
      setIsAdmin(role === "contracts_Admin" || role === "contracts_OrgAdmin");
    } catch {
      setIsAdmin(false);
    }
  }, []);

  // State
  const [orgCertificate, setOrgCertificate] = useState(null);
  const [groups, setGroups] = useState([]);
  const [userCerts, setUserCerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [uploadModal, setUploadModal] = useState({ open: false, level: null, targetId: null, title: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, level: null, targetId: null });
  const [groupMemberModal, setGroupMemberModal] = useState({ open: false, group: null });
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");

  // Upload modal for user-level
  const [userUploadModal, setUserUploadModal] = useState({ open: false, targetId: null });

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = await Parse.Cloud.run("getCertificateStatus", {});

      // Map org
      if (status.org && status.org.hasCustomCertificate) {
        setOrgCertificate({
          hasCustomCertificate: true,
          commonName: status.org.identity?.commonName || "",
          organization: status.org.identity?.organization || "",
          email: status.org.identity?.email || "",
          expirationDate: status.org.expirationDate || null,
          updatedAt: status.org.updatedAt || null
        });
      } else {
        setOrgCertificate(null);
      }

      setGroups(status.groups || []);
      setUserCerts(status.users || []);
    } catch (err) {
      showError(err.message || t("cert-mgmt.load-error", { defaultValue: "Failed to load certificate status." }));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (isAdmin) {
      loadStatus();
    }
  }, [isAdmin, loadStatus]);

  // Upload handler
  const handleUpload = async (pfxBase64, passphrase) => {
    const { level, targetId } = uploadModal;
    const result = await Parse.Cloud.run("uploadCertificate", {
      level,
      targetId: targetId || undefined,
      pfxBase64,
      passphrase
    });
    showSuccess(t("cert-mgmt.upload-success", { defaultValue: "Certificate uploaded successfully." }));
    setUploadModal({ open: false, level: null, targetId: null, title: "" });
    await loadStatus();
    return result;
  };

  // User upload handler
  const handleUserUpload = async (pfxBase64, passphrase) => {
    const { targetId } = userUploadModal;
    await Parse.Cloud.run("uploadCertificate", {
      level: "user",
      targetId,
      pfxBase64,
      passphrase
    });
    showSuccess(t("cert-mgmt.upload-success", { defaultValue: "Certificate uploaded successfully." }));
    setUserUploadModal({ open: false, targetId: null });
    await loadStatus();
  };

  // Remove handler
  const handleRemove = async () => {
    const { level, targetId } = confirmDialog;
    try {
      await Parse.Cloud.run("removeCertificate", {
        level,
        targetId: targetId || undefined
      });
      showSuccess(t("cert-mgmt.remove-success", { defaultValue: "Certificate removed." }));
      setConfirmDialog({ open: false, level: null, targetId: null });
      await loadStatus();
    } catch (err) {
      showError(err.message || t("cert-mgmt.remove-error", { defaultValue: "Failed to remove certificate." }));
    }
  };

  // Create group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setIsCreatingGroup(true);
    try {
      await Parse.Cloud.run("createCertGroup", { name: newGroupName.trim() });
      showSuccess(t("cert-mgmt.group-created", { defaultValue: "Group created." }));
      setNewGroupName("");
      await loadStatus();
    } catch (err) {
      showError(err.message || t("cert-mgmt.group-create-error", { defaultValue: "Failed to create group." }));
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Update group name
  const handleUpdateGroup = async (groupId) => {
    if (!editGroupName.trim()) return;
    try {
      await Parse.Cloud.run("updateCertGroup", { groupId, name: editGroupName.trim() });
      showSuccess(t("cert-mgmt.group-updated", { defaultValue: "Group updated." }));
      setEditingGroup(null);
      setEditGroupName("");
      await loadStatus();
    } catch (err) {
      showError(err.message || t("cert-mgmt.group-update-error", { defaultValue: "Failed to update group." }));
    }
  };

  // Delete group
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState({ open: false, groupId: null });
  const handleDeleteGroup = async () => {
    try {
      await Parse.Cloud.run("deleteCertGroup", { groupId: deleteGroupConfirm.groupId });
      showSuccess(t("cert-mgmt.group-deleted", { defaultValue: "Group deleted." }));
      setDeleteGroupConfirm({ open: false, groupId: null });
      await loadStatus();
    } catch (err) {
      showError(err.message || t("cert-mgmt.group-delete-error", { defaultValue: "Failed to delete group." }));
    }
  };

  if (!isAdmin) {
    return (
      <AppShell>
        <div className="p-6">
          <p className="text-app-ink-muted">
            {t("cert-mgmt.no-access", { defaultValue: "You do not have access to this page." })}
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <h1 className="text-2xl font-bold text-app-ink">
          {t("cert-mgmt.title", { defaultValue: "Certificate Management" })}
        </h1>

        {isLoading ? (
          <p className="text-app-ink-muted">{t("loading", { defaultValue: "Loading..." })}</p>
        ) : (
          <>
            {/* Organization Certificate */}
            <Card>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-app-ink">
                    {t("cert-mgmt.org-certificate", { defaultValue: "Organization Certificate" })}
                  </h2>
                  <p className="mt-1 text-sm text-app-ink-muted">
                    {t("cert-mgmt.org-description", { defaultValue: "Default signing certificate for all users in the organization." })}
                  </p>
                </div>
                <CertificateStatusBadge expirationDate={orgCertificate?.expirationDate} />
              </div>

              {orgCertificate && (
                <div className="mt-4 rounded-lg border border-app-line bg-gray-50 p-3 text-sm">
                  <p><span className="font-medium text-app-ink">{t("cert-mgmt.common-name", { defaultValue: "CN:" })}</span> {orgCertificate.commonName || "—"}</p>
                  <p><span className="font-medium text-app-ink">{t("cert-mgmt.organization", { defaultValue: "Org:" })}</span> {orgCertificate.organization || "—"}</p>
                  <p><span className="font-medium text-app-ink">{t("cert-mgmt.email", { defaultValue: "Email:" })}</span> {orgCertificate.email || "—"}</p>
                  <p><span className="font-medium text-app-ink">{t("cert-mgmt.expiration-date", { defaultValue: "Expiration:" })}</span> {orgCertificate.expirationDate ? new Date(orgCertificate.expirationDate).toLocaleDateString() : "—"}</p>
                  <p><span className="font-medium text-app-ink">{t("cert-mgmt.uploaded-at", { defaultValue: "Uploaded:" })}</span> {orgCertificate.updatedAt ? new Date(orgCertificate.updatedAt).toLocaleDateString() : "—"}</p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() => setUploadModal({
                    open: true,
                    level: "org",
                    targetId: null,
                    title: t("cert-mgmt.upload-org", { defaultValue: "Upload Organization Certificate" })
                  })}
                >
                  <Icon name="plus" className="mr-1.5 h-4 w-4" />
                  {orgCertificate
                    ? t("cert-mgmt.replace", { defaultValue: "Replace" })
                    : t("cert-mgmt.upload", { defaultValue: "Upload" })}
                </Button>
                {orgCertificate && (
                  <Button
                    variant="danger"
                    onClick={() => setConfirmDialog({ open: true, level: "org", targetId: null })}
                  >
                    <Icon name="trash" className="mr-1.5 h-4 w-4" />
                    {t("cert-mgmt.remove", { defaultValue: "Remove" })}
                  </Button>
                )}
              </div>
            </Card>

            {/* Group Certificates */}
            <Card>
              <h2 className="text-lg font-semibold text-app-ink">
                {t("cert-mgmt.group-certificates", { defaultValue: "Group Certificates" })}
              </h2>
              <p className="mt-1 text-sm text-app-ink-muted">
                {t("cert-mgmt.group-description", { defaultValue: "Assign certificates to groups of users." })}
              </p>

              {/* Create group */}
              <div className="mt-4 flex gap-2">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateGroup(); }}
                  placeholder={t("cert-mgmt.group-name-placeholder", { defaultValue: "New group name" })}
                  className="flex-1 rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                />
                <Button onClick={handleCreateGroup} disabled={isCreatingGroup || !newGroupName.trim()}>
                  <Icon name="plus" className="mr-1.5 h-4 w-4" />
                  {t("cert-mgmt.create-group", { defaultValue: "Create" })}
                </Button>
              </div>

              {/* Group list */}
              {groups.length === 0 ? (
                <p className="mt-4 text-sm text-app-ink-muted">
                  {t("cert-mgmt.no-groups", { defaultValue: "No certificate groups yet." })}
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {groups.map((group) => (
                    <li key={group.objectId} className="rounded-xl border border-app-line bg-white p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {editingGroup === group.objectId ? (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={editGroupName}
                                onChange={(e) => setEditGroupName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleUpdateGroup(group.objectId); }}
                                className="flex-1 rounded-lg border border-app-line px-2 py-1 text-sm outline-none focus:border-app-brand"
                              />
                              <Button className="!px-2 !py-1 text-xs" onClick={() => handleUpdateGroup(group.objectId)}>
                                {t("save", { defaultValue: "Save" })}
                              </Button>
                              <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => setEditingGroup(null)}>
                                {t("cancel", { defaultValue: "Cancel" })}
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-app-ink">{group.name}</h3>
                              <span className="text-xs text-app-ink-muted">
                                ({group.memberCount} {t("cert-mgmt.members", { defaultValue: "members" })})
                              </span>
                              <CertificateStatusBadge expirationDate={group.certificate?.expirationDate} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          variant="ghost"
                          className="text-xs"
                          onClick={() => setGroupMemberModal({ open: true, group })}
                        >
                          <Icon name="users" className="mr-1 h-3.5 w-3.5" />
                          {t("cert-mgmt.manage-members", { defaultValue: "Members" })}
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-xs"
                          onClick={() => setUploadModal({
                            open: true,
                            level: "group",
                            targetId: group.objectId,
                            title: t("cert-mgmt.upload-group", { defaultValue: "Upload Group Certificate — {{name}}", name: group.name })
                          })}
                        >
                          <Icon name="plus" className="mr-1 h-3.5 w-3.5" />
                          {group.certificate?.hasCustomCertificate
                            ? t("cert-mgmt.replace", { defaultValue: "Replace" })
                            : t("cert-mgmt.upload-cert", { defaultValue: "Upload Cert" })}
                        </Button>
                        {group.certificate?.hasCustomCertificate && (
                          <Button
                            variant="danger"
                            className="text-xs"
                            onClick={() => setConfirmDialog({ open: true, level: "group", targetId: group.objectId })}
                          >
                            <Icon name="trash" className="mr-1 h-3.5 w-3.5" />
                            {t("cert-mgmt.remove-cert", { defaultValue: "Remove Cert" })}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className="text-xs"
                          onClick={() => { setEditingGroup(group.objectId); setEditGroupName(group.name); }}
                        >
                          <Icon name="edit" className="mr-1 h-3.5 w-3.5" />
                          {t("cert-mgmt.rename", { defaultValue: "Rename" })}
                        </Button>
                        <Button
                          variant="danger"
                          className="text-xs"
                          onClick={() => setDeleteGroupConfirm({ open: true, groupId: group.objectId })}
                        >
                          <Icon name="trash" className="mr-1 h-3.5 w-3.5" />
                          {t("cert-mgmt.delete-group", { defaultValue: "Delete Group" })}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* User Certificates */}
            <Card>
              <h2 className="text-lg font-semibold text-app-ink">
                {t("cert-mgmt.user-certificates", { defaultValue: "User Certificates" })}
              </h2>
              <p className="mt-1 text-sm text-app-ink-muted">
                {t("cert-mgmt.user-description", { defaultValue: "Individual certificates assigned directly to users (highest priority)." })}
              </p>

              {/* User search to assign certificate */}
              <UserCertSearch
                onSelectUser={(user) => {
                  setUserUploadModal({ open: true, targetId: user.objectId });
                }}
                existingUserIds={userCerts.map((u) => u.objectId)}
              />

              {userCerts.length === 0 ? (
                <p className="mt-4 text-sm text-app-ink-muted">
                  {t("cert-mgmt.no-user-certs", { defaultValue: "No user-level certificates configured." })}
                </p>
              ) : (
                <ul className="mt-4 space-y-2">
                  {userCerts.map((user) => (
                    <li key={user.objectId} className="flex items-center justify-between rounded-xl border border-app-line bg-white px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-app-ink">{user.Name}</p>
                        <p className="text-xs text-app-ink-muted">{user.Email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <CertificateStatusBadge expirationDate={user.certificate?.expirationDate} />
                        <Button
                          variant="danger"
                          className="h-8 w-8 !p-0"
                          onClick={() => setConfirmDialog({ open: true, level: "user", targetId: user.objectId })}
                          aria-label={t("cert-mgmt.remove-cert", { defaultValue: "Remove Cert" })}
                        >
                          <Icon name="trash" className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>

      {/* Upload Modal */}
      <CertificateUploadModal
        isOpen={uploadModal.open}
        onClose={() => setUploadModal({ open: false, level: null, targetId: null, title: "" })}
        onUpload={handleUpload}
        title={uploadModal.title}
      />

      {/* User Upload Modal */}
      <CertificateUploadModal
        isOpen={userUploadModal.open}
        onClose={() => setUserUploadModal({ open: false, targetId: null })}
        onUpload={handleUserUpload}
        title={t("cert-mgmt.upload-user", { defaultValue: "Upload User Certificate" })}
      />

      {/* Group Member Modal */}
      <GroupMemberModal
        isOpen={groupMemberModal.open}
        onClose={() => setGroupMemberModal({ open: false, group: null })}
        group={groupMemberModal.group}
        onMembersChanged={loadStatus}
      />

      {/* Confirm Remove Certificate */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={t("cert-mgmt.confirm-remove-title", { defaultValue: "Remove Certificate" })}
        message={t("cert-mgmt.confirm-remove-message", { defaultValue: "Are you sure you want to remove this certificate? This action cannot be undone." })}
        confirmText={t("cert-mgmt.remove", { defaultValue: "Remove" })}
        cancelText={t("cancel", { defaultValue: "Cancel" })}
        type="danger"
        onConfirm={handleRemove}
        onCancel={() => setConfirmDialog({ open: false, level: null, targetId: null })}
      />

      {/* Confirm Delete Group */}
      <ConfirmDialog
        isOpen={deleteGroupConfirm.open}
        title={t("cert-mgmt.confirm-delete-group-title", { defaultValue: "Delete Group" })}
        message={t("cert-mgmt.confirm-delete-group-message", { defaultValue: "Are you sure? All members will be unassigned and the group certificate will be removed." })}
        confirmText={t("cert-mgmt.delete", { defaultValue: "Delete" })}
        cancelText={t("cancel", { defaultValue: "Cancel" })}
        type="danger"
        onConfirm={handleDeleteGroup}
        onCancel={() => setDeleteGroupConfirm({ open: false, groupId: null })}
      />
    </AppShell>
  );
}

export default CertificateManagementPage;
